import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ReferralRequestSettings {
  id: string;
  days_after_won: number;
  max_attempts_per_cycle: number;
  days_between_attempts: number;
  cycle_repeat_days: number;
  max_cycles: number | null; // null = ciclos infinitos
  is_active: boolean;
  exclude_weekends: boolean;
  max_daily_calls_per_consultant: number;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export function useReferralRequestSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['referral-request-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_request_settings')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      return data as ReferralRequestSettings;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<ReferralRequestSettings>) => {
      if (!settings?.id) throw new Error('No settings to update');

      const { data, error } = await supabase
        .from('referral_request_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-request-settings'] });
      toast.success('Configurações salvas com sucesso');
    },
    onError: (error) => {
      console.error('Error updating settings:', error);
      toast.error('Erro ao salvar configurações');
    }
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}
